import type { ReactNode } from "react";
import { memo, useCallback, useMemo } from "react";

import type { PopoverOrigin } from "@mui/material";
import { Popover } from "@mui/material";

import { MenuListPositionOpts } from "shared/types/componentTypes";

import { getAnchorOriginPos, getTransformOriginPos } from "utils/serviceUtils/helpers";

import { paperStyle } from "./style";

interface PopoverBoxProps {
	anchorEl: any;
	show?: boolean;
	onClose?: () => void;
	popoverPosition?: MenuListPositionOpts;
	contentPosition?: MenuListPositionOpts;
	customMinWidth?: number | string;
	customMaxWidth?: number | string;
	customMinHeight?: number | string;
	customMaxHeight?: number | string;
	children?: ReactNode;
	eclipse?: boolean;
	offset?: number[]; // top, right, bottom, left
}

const PopoverBox: React.FC<PopoverBoxProps> = memo(
	({
		anchorEl,
		show = false,
		onClose,
		popoverPosition = MenuListPositionOpts.BOTTOM_LEFT,
		contentPosition = MenuListPositionOpts.BOTTOM_CENTER,
		customMinWidth = 200,
		customMaxWidth,
		customMinHeight = 100,
		customMaxHeight,
		children,
		eclipse = true,
		offset = []
	}) => {
		const handleClose = useCallback(() => {
			onClose && onClose();
		}, [onClose]);

		const anchorOriginPos: PopoverOrigin = useMemo(() => getAnchorOriginPos(contentPosition), [contentPosition]);

		const transformOriginPos: PopoverOrigin = useMemo(() => getTransformOriginPos(popoverPosition), [popoverPosition]);

		const customStyles = useMemo(() => {
			const custom = {
				minWidth: typeof customMinWidth === "number" ? `${customMinWidth}px` : customMinWidth,
				minHeight: typeof customMinHeight === "number" ? `${customMinHeight}px` : customMinHeight,
				"& .MuiList-root": {
					padding: 0
				}
			};
			if (customMaxWidth) {
				custom["maxWidth"] = typeof customMaxWidth === "number" ? `${customMaxWidth}px` : customMaxWidth;
			}
			if (customMaxHeight) {
				custom["maxHeight"] = typeof customMaxHeight === "number" ? `${customMaxHeight}px` : customMaxHeight;
			}

			if (offset.length) {
				offset.forEach((offsetVal, index) => {
					if (index === 0) {
						custom["marginTop"] = typeof offsetVal === "number" ? `${offsetVal}px` : offsetVal;
					}
					if (index === 1) {
						custom["marginRight"] = typeof offsetVal === "number" ? `${offsetVal}px` : offsetVal;
					}
					if (index === 2) {
						custom["marginBottom"] = typeof offsetVal === "number" ? `${offsetVal}px` : offsetVal;
					}
					if (index === 3) {
						custom["marginLeft"] = typeof offsetVal === "number" ? `${offsetVal}px` : offsetVal;
					}
				});
			}
			return custom;
		}, [customMinWidth, customMinHeight, customMaxWidth, customMaxHeight, offset]);

		return (
			<Popover
				open={show}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={anchorOriginPos}
				transformOrigin={transformOriginPos}
				BackdropProps={{
					style: eclipse
						? {
								backgroundColor: "rgba(0,0,0,0.75)"
							}
						: {}
				}}
				PaperProps={{
					style: paperStyle,
					sx: customStyles
				}}
			>
				{children}
			</Popover>
		);
	}
);

export default PopoverBox;
